import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config/apiConfig';
import { useAuth } from '../contexts/AuthContext';

// ─── Diálogo de confirmación estilizado ──────────────────────────────────────
const ConfirmDialog = ({ title, message, onConfirm, onCancel, confirmText = "Confirmar", cancelText = "Cancelar", loading = false, danger = false }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
    <div style={{ background: '#fff', borderRadius: 12, padding: '1.75rem 2rem', width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>{title}</h3>
      <p style={{ margin: '0 0 1.5rem', color: '#64748b', fontSize: '0.95rem', lineHeight: '1.5' }}>{message}</p>
      <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '0.45rem 1.1rem', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, cursor: 'pointer' }}>{cancelText}</button>
        <button onClick={onConfirm} disabled={loading} style={{ padding: '0.45rem 1.3rem', borderRadius: 6, border: 'none', background: danger ? '#f43f5e' : '#3498db', color: '#fff', fontWeight: 600, cursor: loading ? 'wait' : 'pointer' }}>{loading ? '...' : confirmText}</button>
      </div>
    </div>
  </div>
);

// ─── Componentes de Mensajes ───────────────────────────────────────────────
const MsgError = ({ msg }) => msg ? (
  <div style={{ background: '#fff1f2', borderLeft: '4px solid #f43f5e', color: '#9f1239', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.9rem' }}>{msg}</div>
) : null;

const MsgExito = ({ msg }) => msg ? (
  <div style={{ background: '#f0fdf4', borderLeft: '4px solid #38a169', color: '#276749', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.9rem' }}>{msg}</div>
) : null;

const BarrasAdminComponent = () => {
  const { auth } = useAuth();
  const [tab, setTab] = useState('barras');

  const [barras, setBarras] = useState([]);
  const [loadingBarras, setLoadingBarras] = useState(true);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTipo, setNuevoTipo] = useState('mixto');
  const [creando, setCreando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [editandoNombre, setEditandoNombre] = useState('');
  const [editandoTipo, setEditandoTipo] = useState('mixto');
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);

  const [cocineros, setCocineros] = useState([]);
  const [loadingCocineros, setLoadingCocineros] = useState(false);
  const [guardandoAsignacion, setGuardandoAsignacion] = useState({});

  const [nuevoNombreC, setNuevoNombreC] = useState('');
  const [nuevoApellidoC, setNuevoApellidoC] = useState('');
  const [nuevoUsuarioC, setNuevoUsuarioC] = useState('');
  const [nuevoPasswordC, setNuevoPasswordC] = useState('');
  const [nuevoIdBarraC, setNuevoIdBarraC] = useState('');
  const [creandoCocinero, setCreandoCocinero] = useState(false);

  const [error, setError] = useState('');
  const [exito, setExito] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, onConfirm, danger, confirmText }

  const mostrarExito = (msg) => { setExito(msg); setError(''); setTimeout(() => setExito(''), 3000); };
  const mostrarError = (msg) => { setError(msg); setExito(''); };

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth?.token}` };

  const inputStyle = {
    padding: '0.45rem 0.75rem', borderRadius: 6,
    border: '1.5px solid #e2e8f0', background: '#fff',
    color: '#2d3748', fontSize: '0.9rem', fontFamily: 'inherit',
  };

  const fetchBarras = async () => {
    setLoadingBarras(true);
    try {
      const res = await fetch(getApiUrl('/barras/todas'), { headers });
      if (!res.ok) throw new Error('Error al cargar barras');
      setBarras(await res.json());
    } catch (e) { mostrarError(e.message); }
    finally { setLoadingBarras(false); }
  };

  const fetchCocineros = async () => {
    setLoadingCocineros(true);
    try {
      const res = await fetch(getApiUrl('/barras/cocineros'), { headers });
      if (!res.ok) throw new Error('Error al cargar cocineros');
      setCocineros(await res.json());
    } catch (e) { mostrarError(e.message); }
    finally { setLoadingCocineros(false); }
  };

  useEffect(() => { fetchBarras(); }, []);
  useEffect(() => { if (tab === 'cocineros') fetchCocineros(); }, [tab]);

  const handleCrear = async (e) => {
    e.preventDefault();
    if (!nuevoNombre.trim()) return;
    setCreando(true);
    try {
      const res = await fetch(getApiUrl('/barras'), {
        method: 'POST', headers, body: JSON.stringify({ nombre: nuevoNombre.trim(), tipo: nuevoTipo }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Error al crear barra'); }
      const nueva = await res.json();
      setBarras(prev => [...prev, nueva].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setNuevoNombre(''); setNuevoTipo('mixto');
      mostrarExito(`Barra "${nueva.nombre}" creada.`);
    } catch (e) { mostrarError(e.message); }
    finally { setCreando(false); }
  };

  const handleGuardarEdicion = async (id) => {
    if (!editandoNombre.trim()) return;
    setGuardandoEdicion(true);
    try {
      const res = await fetch(getApiUrl(`/barras/${id}`), {
        method: 'PUT', headers, body: JSON.stringify({ nombre: editandoNombre.trim(), tipo: editandoTipo }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Error al editar'); }
      const actualizada = await res.json();
      setBarras(prev => prev.map(b => b.id === id ? { ...b, nombre: actualizada.nombre, tipo: actualizada.tipo } : b));
      setEditandoId(null);
      mostrarExito('Barra actualizada.');
    } catch (e) { mostrarError(e.message); }
    finally { setGuardandoEdicion(false); }
  };

  const handleDesactivar = (id, nombre) => {
    setConfirmDialog({
      title: 'Desactivar barra',
      message: `¿Desactivar la barra "${nombre}"? Los usuarios asignados no podrán verla hasta que se reactive.`,
      confirmText: 'Desactivar',
      danger: true,
      onConfirm: async () => {
        try {
          const res = await fetch(getApiUrl(`/barras/${id}/desactivar`), { method: 'PATCH', headers });
          if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Error al desactivar'); }
          setBarras(prev => prev.map(b => b.id === id ? { ...b, activo: false } : b));
          mostrarExito(`Barra "${nombre}" desactivada.`);
        } catch (e) { mostrarError(e.message); }
        finally { setConfirmDialog(null); }
      }
    });
  };

  const handleActivar = async (id, nombre) => {
    try {
      const res = await fetch(getApiUrl(`/barras/${id}/activar`), { method: 'PATCH', headers });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Error al activar'); }
      setBarras(prev => prev.map(b => b.id === id ? { ...b, activo: true } : b));
      mostrarExito(`Barra "${nombre}" reactivada.`);
    } catch (e) { mostrarError(e.message); }
  };

  const ejecutarEliminar = async (id, nombre, conFirmarHistoricos = false) => {
    const url = conFirmarHistoricos
      ? getApiUrl(`/barras/${id}/eliminar?confirmar_historicos=true`)
      : getApiUrl(`/barras/${id}/eliminar`);
    
    try {
      const res = await fetch(url, { method: 'DELETE', headers });
      const data = await res.json();
      if (!res.ok) {
        if (data.tipo === 'advertencia') {
          setConfirmDialog({
            title: '¿Confirmar eliminación?',
            message: `⚠️ ${data.message}`,
            confirmText: 'Eliminar de todas formas',
            danger: true,
            onConfirm: () => ejecutarEliminar(id, nombre, true)
          });
        } else {
          mostrarError(data.message || 'Error al eliminar');
          setConfirmDialog(null);
        }
      } else {
        setBarras(prev => prev.filter(b => b.id !== id));
        mostrarExito(data.message);
        setConfirmDialog(null);
      }
    } catch (e) {
      mostrarError(e.message);
      setConfirmDialog(null);
    }
  };

  const handleEliminar = (id, nombre) => {
    setConfirmDialog({
      title: 'Eliminar barra permanentemente',
      message: `Esta acción borrará la barra "${nombre}" definitivamente. Solo es posible si no tiene pedidos pendientes ni usuarios asignados activos.`,
      confirmText: 'Eliminar permanentemente',
      danger: true,
      onConfirm: () => ejecutarEliminar(id, nombre)
    });
  };

  const handleCrearCocinero = async (e) => {
    e.preventDefault();
    if (!nuevoNombreC.trim() || !nuevoUsuarioC.trim() || !nuevoPasswordC.trim()) return;
    setCreandoCocinero(true);
    try {
      const res = await fetch(getApiUrl('/barras/cocineros/nuevo'), {
        method: 'POST', headers,
        body: JSON.stringify({
          nombre: nuevoNombreC.trim(), apellido: nuevoApellidoC.trim(),
          usuario: nuevoUsuarioC.trim(), contraseña: nuevoPasswordC,
          id_barra: nuevoIdBarraC ? parseInt(nuevoIdBarraC) : null,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Error al crear cocinero'); }
      const nuevo = await res.json();
      const barraAsignada = barras.find(b => b.id === nuevo.id_barra_asignada);
      setCocineros(prev => [...prev, { ...nuevo, nombre_barra: barraAsignada?.nombre || null }]
        .sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setNuevoNombreC(''); setNuevoApellidoC(''); setNuevoUsuarioC('');
      setNuevoPasswordC(''); setNuevoIdBarraC('');
      mostrarExito(`Cocinero "${nuevo.nombre}" creado.`);
    } catch (e) { mostrarError(e.message); }
    finally { setCreandoCocinero(false); }
  };

  const handleAsignarBarra = async (idUsuario, idBarra) => {
    setGuardandoAsignacion(prev => ({ ...prev, [idUsuario]: true }));
    try {
      const res = await fetch(getApiUrl(`/barras/cocineros/${idUsuario}`), {
        method: 'PUT', headers, body: JSON.stringify({ id_barra: idBarra || null }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.message || 'Error'); }
      const barraSeleccionada = barras.find(b => b.id === idBarra);
      setCocineros(prev => prev.map(c => c.id !== idUsuario ? c : {
        ...c, id_barra_asignada: idBarra || null, nombre_barra: barraSeleccionada?.nombre || null,
      }));
      mostrarExito('Asignación guardada.');
    } catch (e) { mostrarError(e.message); }
    finally { setGuardandoAsignacion(prev => ({ ...prev, [idUsuario]: false })); }
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', marginBottom: '1.25rem', marginTop: 0, borderLeft: '3px solid #f05b41', paddingLeft: '0.75rem' }}>
        Configuración de Barras / Cocinas
      </h2>

      {/* Tabs */}
      <div style={{ display: 'flex', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0' }}>
        {[
          { key: 'barras', label: 'Barras / Cocinas' },
          { key: 'cocineros', label: 'Asignación de Cocineros' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '0.5rem 1.25rem', border: 'none',
              borderBottom: tab === t.key ? '3px solid #f05b41' : '3px solid transparent',
              background: 'transparent',
              color: tab === t.key ? '#f05b41' : '#64748b',
              fontWeight: tab === t.key ? 700 : 500,
              cursor: 'pointer', fontSize: '0.9rem',
              marginBottom: '-2px', fontFamily: 'inherit',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <MsgError msg={error} />
      <MsgExito msg={exito} />

      {/* ── TAB: Barras ── */}
      {tab === 'barras' && (
        <div>
          {/* Formulario nueva barra */}
          <form onSubmit={handleCrear} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Nombre de la nueva barra/cocina..."
              value={nuevoNombre}
              onChange={e => setNuevoNombre(e.target.value)}
              style={{ flex: 1, minWidth: '180px', ...inputStyle }}
            />
            <select
              value={nuevoTipo}
              onChange={e => setNuevoTipo(e.target.value)}
              style={{ ...inputStyle, width: 'auto' }}
              title="Tipo: determina qué secciones ven los usuarios asignados"
            >
              <option value="mixto">Mixto (Cocina + Barra)</option>
              <option value="cocina">Solo Cocina</option>
              <option value="barra">Solo Barra</option>
            </select>
            <button
              type="submit"
              disabled={creando || !nuevoNombre.trim()}
              style={{
                padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none',
                background: creando || !nuevoNombre.trim() ? '#e2e8f0' : 'linear-gradient(to right, #f05b41, #f78361)',
                color: creando || !nuevoNombre.trim() ? '#94a3b8' : '#fff',
                fontWeight: 600, cursor: creando || !nuevoNombre.trim() ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem', transition: 'background 0.2s',
              }}
            >
              {creando ? 'Creando...' : '+ Nueva Barra'}
            </button>
          </form>

          {/* Lista de barras */}
          {loadingBarras ? (
            <p style={{ color: '#64748b' }}>Cargando...</p>
          ) : barras.length === 0 ? (
            <p style={{ color: '#64748b' }}>No hay barras creadas aún.</p>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <table style={{ minWidth: '600px', width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Nombre</th>
                    <th style={{ textAlign: 'center', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Tipo</th>
                    <th style={{ textAlign: 'center', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Estado</th>
                    <th style={{ textAlign: 'right', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {barras.map(barra => {
                    const tipoBadge = {
                      cocina: { bg: '#dbeafe', color: '#1d4ed8', label: 'Cocina' },
                      barra:  { bg: '#fff7ed', color: '#c2410c', label: 'Barra' },
                      mixto:  { bg: '#f1f5f9', color: '#475569', label: 'Mixto' },
                    }[barra.tipo || 'mixto'];
                    return (
                      <tr key={barra.id} style={{ borderBottom: '1px solid #e2e8f0', opacity: barra.activo ? 1 : 0.5 }}>
                        <td style={{ padding: '0.7rem 1rem', color: '#1e293b', fontWeight: 500 }}>
                          {editandoId === barra.id ? (
                            <input
                              autoFocus
                              value={editandoNombre}
                              onChange={e => setEditandoNombre(e.target.value)}
                              onKeyDown={e => e.key === 'Escape' && setEditandoId(null)}
                              style={{ ...inputStyle, border: '1.5px solid #f05b41' }}
                            />
                          ) : (
                            barra.nombre
                          )}
                        </td>
                        <td style={{ textAlign: 'center', padding: '0.7rem 1rem' }}>
                          {editandoId === barra.id ? (
                            <select
                              value={editandoTipo}
                              onChange={e => setEditandoTipo(e.target.value)}
                              style={{ ...inputStyle, width: 'auto', fontSize: '0.82rem' }}
                            >
                              <option value="mixto">Mixto</option>
                              <option value="cocina">Cocina</option>
                              <option value="barra">Barra</option>
                            </select>
                          ) : (
                            <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.65rem', borderRadius: 999, fontWeight: 600, background: tipoBadge.bg, color: tipoBadge.color }}>
                              {tipoBadge.label}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center', padding: '0.7rem 1rem' }}>
                          <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.65rem', borderRadius: 999, fontWeight: 600, background: barra.activo ? '#dcfce7' : '#f1f5f9', color: barra.activo ? '#15803d' : '#94a3b8' }}>
                            {barra.activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', padding: '0.7rem 1rem' }}>
                          {editandoId === barra.id ? (
                            <>
                              <button onClick={() => handleGuardarEdicion(barra.id)} disabled={guardandoEdicion} style={{ marginRight: '0.5rem', padding: '0.3rem 0.75rem', borderRadius: 6, border: 'none', background: '#38a169', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>{guardandoEdicion ? '...' : 'Guardar'}</button>
                              <button onClick={() => setEditandoId(null)} style={{ padding: '0.3rem 0.75rem', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: '0.85rem' }}>Cancelar</button>
                            </>
                          ) : (
                            <>
                              {barra.activo ? (
                                <>
                                  <button onClick={() => { setEditandoId(barra.id); setEditandoNombre(barra.nombre); setEditandoTipo(barra.tipo || 'mixto'); }} style={{ marginRight: '0.5rem', padding: '0.3rem 0.75rem', borderRadius: 6, border: 'none', background: '#fef3c7', color: '#92400e', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>Editar</button>
                                  <button onClick={() => handleDesactivar(barra.id, barra.nombre)} style={{ padding: '0.3rem 0.75rem', borderRadius: 6, border: '1px solid #fca5a5', background: '#fee2e2', color: '#dc2626', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>Desactivar</button>
                                </>
                              ) : (
                                <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                                  <button onClick={() => handleActivar(barra.id, barra.nombre)} title="Reactivar esta barra" style={{ padding: '0.3rem 0.75rem', borderRadius: 6, border: '1px solid #86efac', background: '#dcfce7', color: '#15803d', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>✓ Activar</button>
                                  <button onClick={() => handleEliminar(barra.id, barra.nombre)} title="Eliminar permanentemente esta barra" style={{ padding: '0.3rem 0.75rem', borderRadius: 6, border: '1px solid #fca5a5', background: '#fee2e2', color: '#dc2626', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>🗑️ Eliminar</button>
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Cocineros ── */}
      {tab === 'cocineros' && (
        <div>
          {/* Formulario nuevo cocinero */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '1.25rem 1.5rem', marginBottom: '1.5rem', boxShadow: '0 4px 15px rgba(0,0,0,0.07)' }}>
            <h3 style={{ color: '#1e293b', fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', marginTop: 0, borderLeft: '3px solid #f05b41', paddingLeft: '0.75rem' }}>Crear nuevo cocinero</h3>
            <form onSubmit={handleCrearCocinero}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.6rem', marginBottom: '1rem' }}>
                <input type="text" placeholder="Nombre *" value={nuevoNombreC} onChange={e => setNuevoNombreC(e.target.value)} required style={inputStyle} />
                <input type="text" placeholder="Apellido" value={nuevoApellidoC} onChange={e => setNuevoApellidoC(e.target.value)} style={inputStyle} />
                <input type="text" placeholder="Usuario *" value={nuevoUsuarioC} onChange={e => setNuevoUsuarioC(e.target.value)} required style={inputStyle} />
                <input type="password" placeholder="Contraseña *" value={nuevoPasswordC} onChange={e => setNuevoPasswordC(e.target.value)} required style={inputStyle} />
                <select value={nuevoIdBarraC} onChange={e => setNuevoIdBarraC(e.target.value)} style={inputStyle}>
                  <option value="">— Sin barra asignada —</option>
                  {barras.filter(b => b.activo).map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
              </div>
              <button type="submit" disabled={creandoCocinero || !nuevoNombreC.trim() || !nuevoUsuarioC.trim() || !nuevoPasswordC.trim()} style={{ padding: '0.5rem 1.5rem', borderRadius: 8, border: 'none', background: (creandoCocinero || !nuevoNombreC.trim() || !nuevoUsuarioC.trim() || !nuevoPasswordC.trim()) ? '#e2e8f0' : 'linear-gradient(to right, #f05b41, #f78361)', color: (creandoCocinero || !nuevoNombreC.trim() || !nuevoUsuarioC.trim() || !nuevoPasswordC.trim()) ? '#94a3b8' : '#fff', fontWeight: 600, cursor: creandoCocinero ? 'wait' : 'pointer', fontSize: '0.9rem' }}>{creandoCocinero ? 'Creando...' : '+ Nuevo Cocinero'}</button>
            </form>
          </div>

          {/* Lista cocineros */}
          {loadingCocineros ? (
            <p style={{ color: '#64748b' }}>Cargando cocineros...</p>
          ) : cocineros.length === 0 ? (
            <p style={{ color: '#64748b' }}>No hay usuarios con rol COCINERO registrados.</p>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflowX: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <table style={{ minWidth: '500px', width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Cocinero</th>
                    <th style={{ textAlign: 'left', padding: '0.6rem 1rem', color: '#64748b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: 600 }}>Barra / Cocina Asignada</th>
                  </tr>
                </thead>
                <tbody>
                  {cocineros.map(cocinero => (
                    <tr key={cocinero.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ color: '#1e293b', fontWeight: 500 }}>{cocinero.nombre} {cocinero.apellido}</div>
                        <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>@{cocinero.usuario}</div>
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <select value={cocinero.id_barra_asignada || ''} onChange={e => handleAsignarBarra(cocinero.id, e.target.value ? parseInt(e.target.value) : null)} disabled={guardandoAsignacion[cocinero.id]} style={{ ...inputStyle, minWidth: '180px', width: 'auto' }}>
                          <option value="">— Sin asignar —</option>
                          {barras.filter(b => b.activo).map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                        </select>
                        {guardandoAsignacion[cocinero.id] && <span style={{ marginLeft: '0.5rem', color: '#94a3b8', fontSize: '0.8rem' }}>Guardando...</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Confirm Dialog ── */}
      {confirmDialog && <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog(null)} />}
    </div>
  );
};

export default BarrasAdminComponent;
